import { useEffect, useRef, useState } from "react";
import type { ActivityLogEntry } from "../../hooks/useActivityLog";
import styles from "./ActivityLog.module.css";

export interface ActivityLogProps {
  entries: ActivityLogEntry[];
  defaultExpanded?: boolean;
}

const LEVEL_CLASS: Record<ActivityLogEntry["level"], string> = {
  info: styles.dotInfo,
  success: styles.dotSuccess,
  warning: styles.dotWarning,
  error: styles.dotError,
};

function formatTime(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export function ActivityLog({
  entries,
  defaultExpanded = false,
}: ActivityLogProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (expanded && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [entries.length, expanded]);

  return (
    <div className={styles.section}>
      <button
        className={styles.collapseToggle}
        onClick={() => setExpanded(!expanded)}
        type="button"
      >
        Activity Log ({entries.length})
        <span className={styles.toggleIcon}>
          {expanded ? "\u25B2" : "\u25BC"}
        </span>
      </button>
      {expanded && (
        <ul
          ref={listRef}
          className={styles.list}
          role="log"
          aria-live="polite"
        >
          {entries.map((entry) => (
            <li key={entry.id} className={styles.entry}>
              <span className={styles.timestamp}>
                [{formatTime(entry.timestamp)}]
              </span>
              <span
                className={`${styles.levelDot} ${LEVEL_CLASS[entry.level]}`}
                data-testid={`level-dot-${entry.level}`}
              />
              <span className={styles.message}>{entry.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
